import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Activate.css';

interface DeviceInfo {
  device_name: string;
  vehicle_type: string;
  package_type: string;
}

const VEHICLE_LABELS: Record<string, string> = {
  motorcycle: 'Motocykl',
  bicycle: 'Rower',
  scooter: 'Skuter',
  car: 'Samochód',
};

const PACKAGE_LABELS: Record<string, string> = {
  standard: 'Standard (IP54)',
  premium: 'Premium (IP67, 8 dni baterii)',
};

type Screen = 'loading' | 'invalid' | 'info' | 'register' | 'success';

export default function Activate() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  useAuth();

  const token = params.get('token') || '';
  const [screen, setScreen] = useState<Screen>('loading');
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo | null>(null);

  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', password: '', phone: '',
  });
  const [errors, setErrors] = useState<Partial<typeof form>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!token) { setScreen('invalid'); return; }
    fetch('/api/activate/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then(async (r) => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then((data: DeviceInfo) => {
        setDeviceInfo(data);
        setScreen('info');
      })
      .catch(() => setScreen('invalid'));
  }, [token]);

  const validate = () => {
    const e: Partial<typeof form> = {};
    if (!form.first_name.trim()) e.first_name = 'Wymagane';
    if (!form.last_name.trim()) e.last_name = 'Wymagane';
    if (!form.email.match(/^[^@]+@[^@]+\.[^@]+$/)) e.email = 'Nieprawidłowy email';
    if (form.password.length < 8) e.password = 'Min. 8 znaków';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const r = await fetch('/api/activate/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, ...form }),
      });
      const data = await r.json();
      if (!r.ok) {
        setSubmitError(data.detail || 'Błąd aktywacji');
        return;
      }
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      setScreen('success');
    } catch {
      setSubmitError('Błąd połączenia. Spróbuj ponownie.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoToApp = () => navigate('/', { replace: true });

  if (screen === 'loading') {
    return (
      <div className="activate-page">
        <div className="activate-card">
          <div className="activate-spinner" />
          <p className="activate-hint">Sprawdzanie linku aktywacyjnego...</p>
        </div>
      </div>
    );
  }

  if (screen === 'invalid') {
    return (
      <div className="activate-page">
        <div className="activate-card">
          <div className="activate-icon error">✕</div>
          <h2>Link nieważny</h2>
          <p className="activate-hint">Link aktywacyjny jest nieprawidłowy lub wygasł.<br />Skontaktuj się ze sprzedawcą.</p>
        </div>
      </div>
    );
  }

  if (screen === 'info' && deviceInfo) {
    return (
      <div className="activate-page">
        <div className="activate-card">
          <div className="activate-logo">
            <span className="logo-10">10</span><span className="logo-drive">drive</span>
            <span className="logo-moto">moto</span>
          </div>
          <h2>Aktywacja urządzenia</h2>
          <div className="activate-device-info">
            <div className="info-row">
              <span className="info-label">Urządzenie</span>
              <span className="info-value">{deviceInfo.device_name}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Typ pojazdu</span>
              <span className="info-value">{VEHICLE_LABELS[deviceInfo.vehicle_type] || deviceInfo.vehicle_type}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Pakiet</span>
              <span className="info-value">{PACKAGE_LABELS[deviceInfo.package_type] || deviceInfo.package_type}</span>
            </div>
          </div>
          <p className="activate-hint">Utwórz konto aby aktywować urządzenie i abonament roczny.</p>
          <button className="activate-btn primary" onClick={() => setScreen('register')}>
            Dalej →
          </button>
        </div>
      </div>
    );
  }

  if (screen === 'register') {
    return (
      <div className="activate-page">
        <div className="activate-card">
          <button className="activate-back" onClick={() => setScreen('info')}>← Wróć</button>
          <h2>Utwórz konto</h2>
          <form className="activate-form" onSubmit={handleSubmit} noValidate>
            <div className="form-row">
              <div className="form-field">
                <label>Imię</label>
                <input
                  type="text" autoComplete="given-name"
                  value={form.first_name}
                  onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                  className={errors.first_name ? 'error' : ''}
                />
                {errors.first_name && <span className="field-error">{errors.first_name}</span>}
              </div>
              <div className="form-field">
                <label>Nazwisko</label>
                <input
                  type="text" autoComplete="family-name"
                  value={form.last_name}
                  onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                  className={errors.last_name ? 'error' : ''}
                />
                {errors.last_name && <span className="field-error">{errors.last_name}</span>}
              </div>
            </div>
            <div className="form-field">
              <label>Email</label>
              <input
                type="email" autoComplete="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className={errors.email ? 'error' : ''}
              />
              {errors.email && <span className="field-error">{errors.email}</span>}
            </div>
            <div className="form-field">
              <label>Hasło</label>
              <input
                type="password" autoComplete="new-password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                className={errors.password ? 'error' : ''}
              />
              {errors.password && <span className="field-error">{errors.password}</span>}
            </div>
            <div className="form-field">
              <label>Telefon <span className="optional">(opcjonalnie)</span></label>
              <input
                type="tel" autoComplete="tel"
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              />
            </div>
            {submitError && <p className="activate-error">{submitError}</p>}
            <button type="submit" className="activate-btn primary" disabled={submitting}>
              {submitting ? <span className="activate-spinner small" /> : 'Utwórz konto i aktywuj'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="activate-page">
      <div className="activate-card">
        <div className="activate-icon success">✓</div>
        <h2>Urządzenie aktywowane!</h2>
        <p className="activate-hint">Twój abonament jest aktywny przez 365 dni.</p>
        <button className="activate-btn primary" onClick={handleGoToApp}>
          Przejdź do aplikacji →
        </button>
      </div>
    </div>
  );
}
